import { AnimatePresence } from 'framer-motion';
import { FC, memo, useEffect, useState } from 'react';

import useAlerts from '@/hooks/useAlerts';
import {
  downloadImagesAsZip,
  downloadSelectedImagesAsZip,
  generateImagesInParallel,
  generateSingleImage,
} from '@/utils/coverImages/imageGeneration';
import { distributeTextCanvas } from '@/utils/coverImages/textDistribution';
import { replaceQuotes } from '@/utils/helpers';

import { ImageVariation } from '.';
import FloatingBox from '../FloatingBox';
import ImageResults from '../ImageResults';
import PostToArticleButton from '../PostToArticleButton';
import Progress from '../Progress';
import { createWatermarkTagImage } from '../shared';

interface Props {
  variations?: ImageVariation[];
  images?: string[];
  setImages: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  inArticle?: boolean;
  onReset: () => void;
}

const GeneratedImages: FC<Props> = memo(
  ({ variations, images, setImages, inArticle, onReset }) => {
    const alerts = useAlerts();

    const [selectedImages, setSelectedImages] = useState<number[]>([]);
    const [counter, setCounter] = useState(0);
    const [showProgress, setShowProgress] = useState(false);

    const generateImageVariation = async (
      variation: ImageVariation,
    ): Promise<string | undefined> => {
      try {
        return await generateSingleImage(
          () => {
            const imageHolder = document.createElement('div');
            imageHolder.classList.add('image-holder');

            const leftImage = new Image();
            leftImage.src = variation.leftImageSrc;
            leftImage.classList.add('image');

            const rightImage = new Image();
            rightImage.src = variation.rightImageSrc;
            rightImage.classList.add('image');

            const leftDiv = document.createElement('div');
            leftDiv.classList.add('left-div');

            const rightDiv = document.createElement('div');
            rightDiv.classList.add('right-div');

            leftDiv.appendChild(leftImage);

            rightDiv.appendChild(rightImage);

            const leftWatermark = createWatermarkTagImage(
              'left-actual-photo',
              variation.leftWatermarkTag,
              variation.leftWatermarkPosition,
            );
            if (leftWatermark) leftDiv.appendChild(leftWatermark);

            const rightWatermark = createWatermarkTagImage(
              'right-actual-photo',
              variation.rightWatermarkTag,
              variation.rightWatermarkPosition,
            );
            if (rightWatermark) rightDiv.appendChild(rightWatermark);

            if (variation.divider !== 'none') {
              const middleBreak = document.createElement('div');

              middleBreak.classList.add('break');

              if (variation.divider === 'black') {
                middleBreak.classList.add('break--black');
              }

              imageHolder.appendChild(middleBreak);
            }

            if (variation.leftText) {
              const leftText = document.createElement('div');
              leftText.classList.add('left-text');

              if (variation.divider !== 'none') {
                leftText.classList.add('left-text__divider');
              }

              const leftP = document.createElement('p');
              const updatedText = replaceQuotes(variation.leftText);
              let text = '';
              if (variation.leftQuotes) {
                text = `“${updatedText}”`;
              } else {
                text = updatedText;
              }

              if (variation.leftBreakes) {
                leftP.innerText = text;
                leftP.classList.add('custom-brakes');
              } else {
                leftP.innerHTML = distributeTextCanvas(text, {
                  maxWidth: 465,
                  fontFamily: 'CustomArial',
                  minFontSize: 45,
                });
              }

              leftText.appendChild(leftP);

              if (variation.leftTextPositon === 'top') {
                leftText.classList.add('left-text--top');
              }

              leftDiv.appendChild(leftText);
            }

            if (variation.rightText) {
              const rightText = document.createElement('div');
              rightText.classList.add('right-text');

              if (variation.divider !== 'none') {
                rightText.classList.add('right-text__divider');
              }

              const rightP = document.createElement('p');
              const updatedText = replaceQuotes(variation.rightText);
              let text = '';
              if (variation.rightQuotes) {
                text = `“${updatedText}”`;
              } else {
                text = updatedText;
              }

              if (variation.rightBreakes) {
                rightP.innerText = text;
                rightP.classList.add('custom-brakes');
              } else {
                rightP.innerHTML = distributeTextCanvas(text, {
                  maxWidth: 465,
                  fontFamily: 'CustomArial',
                  minFontSize: 45,
                });
              }

              rightText.appendChild(rightP);

              if (variation.rightTextPositon === 'top') {
                rightText.classList.add('right-text--top');
              }

              rightDiv.appendChild(rightText);
            }

            imageHolder.appendChild(leftDiv);
            imageHolder.appendChild(rightDiv);

            return imageHolder;
          },
          {
            holderClassName: 'generated-images-holder',
            pixelRatio: 1,
          },
        );
      } catch (e) {
        alerts.error();
        return undefined;
      }
    };

    const generateAllVariations = async () => {
      if (!variations?.length) return;

      setShowProgress(true);
      setCounter(0);

      try {
        const validImages = await generateImagesInParallel(
          variations,
          generateImageVariation,
          (current) => {
            setCounter(current);
          },
        );

        setImages(validImages);
      } finally {
        setShowProgress(false);
        setCounter(0);
      }
    };

    const downloadPlainImages = async () => {
      if (!variations?.length) return;

      setShowProgress(true);

      const foundImages = selectedImages.map(
        (selectedIndex: number) => variations[selectedIndex],
      );

      try {
        const generatedImages = await generateImagesInParallel(
          foundImages,
          async (variation: ImageVariation) => {
            return await generateSingleImage(
              () => {
                const imageHolder = document.createElement('div');
                imageHolder.classList.add('image-holder');

                const leftImage = new Image();
                leftImage.src = variation.leftImageSrc;
                leftImage.classList.add('image');

                const rightImage = new Image();
                rightImage.src = variation.rightImageSrc;
                rightImage.classList.add('image');

                const leftDiv = document.createElement('div');
                leftDiv.classList.add('left-div');

                const rightDiv = document.createElement('div');
                rightDiv.classList.add('right-div');

                leftDiv.appendChild(leftImage);

                rightDiv.appendChild(rightImage);

                if (variation.divider !== 'none') {
                  const middleBreak = document.createElement('div');

                  middleBreak.classList.add('break');

                  if (variation.divider === 'black') {
                    middleBreak.classList.add('break--black');
                  }

                  imageHolder.appendChild(middleBreak);
                }

                imageHolder.appendChild(leftDiv);
                imageHolder.appendChild(rightDiv);

                return imageHolder;
              },
              {
                holderClassName: 'generated-images-holder',
                pixelRatio: 1,
              },
            );
          },
          (current) => {
            setCounter(current);
          },
        );

        await downloadImagesAsZip(generatedImages, {
          filename: 'generated-plain-images.zip',
          imagePrefix: 'image-',
        });
      } finally {
        setShowProgress(false);
        setCounter(0);
      }
    };

    const handleDownloadZip = async (selected: boolean) => {
      if (!images?.length) return;

      if (selected) {
        await downloadSelectedImagesAsZip(images, selectedImages, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      } else {
        await downloadImagesAsZip(images, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      }
    };

    useEffect(() => {
      generateAllVariations();
      // eslint-disable-next-line
    }, [variations]);

    useEffect(() => {
      if (!images?.length) return;
      setSelectedImages(images?.map((_, index) => index));
    }, [images]);

    return (
      <>
        <ImageResults
          images={images}
          selectedImages={selectedImages}
          setSelectedImages={setSelectedImages}
          setImages={setImages}
        />
        <AnimatePresence>
          {!!showProgress && !!variations?.length && (
            <Progress
              key={1}
              progressPercent={(counter / variations.length) * 100}
            />
          )}
          {!inArticle ? (
            <>
              {!!images?.length && (
                <FloatingBox
                  key={2}
                  selectedImages={selectedImages}
                  unselectAll={() => setSelectedImages([])}
                  selectAll={() =>
                    setSelectedImages(images?.map((_, index) => index))
                  }
                  onDownloadPlainImages={downloadPlainImages}
                  onDownload={handleDownloadZip}
                  images={images}
                />
              )}
            </>
          ) : (
            <>
              {!!images?.length && (
                <PostToArticleButton
                  images={images}
                  selectedImages={selectedImages}
                  onReset={onReset}
                />
              )}
            </>
          )}
        </AnimatePresence>
      </>
    );
  },
);

export default GeneratedImages;
